import { Box, Button, Chip, FormControl, Grid, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DataGridProProps, GridColumns, useGridApiRef } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { default as React, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useTab } from 'src/components/hooks/useTab';
import { useUser } from 'src/components/hooks/useUser';
import { setMessage } from 'src/store/Message';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import './DataMonitoring.scss';
import { UseDataMonitoringPage } from './UseDataMonitoringPage';
import { DataTable } from '@pihealth/common-ui-library';

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

const DataMonitoringPage: React.FunctionComponent<any> = () => {
  const { setTabVal, tabVal } = useTab();
  const apiRef = useGridApiRef();
  const [monitoringDetails, setMonitoringDetails] = useState<any>([]);
  const [updateMonitoringDetails, setUpdateMonitoringDetails] = useState<any>([]);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_risk_score') && currentHierarchyLevel === 'study';
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const { user } = useUser();
  const { getDataMonitoringData, updateDataMonitoring, monitoringData, monitoringDataLoading } =
    UseDataMonitoringPage();
  const handleChange = (event: any, row: any) => {
    const data = event.target.value;
    const dataName = event.target.name;
    let updateObj: any = null;
    const clonedmonitoringDetails: any = monitoringDetails.map((obj: any) => {
      if (obj.id === row.id) {
        obj[dataName] = data;
        if (
          obj.impact != '' &&
          obj.impact != null &&
          obj.probability != '' &&
          obj.probability != null &&
          obj.detectability != '' &&
          obj.detectability != null
        ) {
          if (obj.hierarchy.length == 1) {
            obj.total_score = Math.round(obj.impact * obj.probability * obj.detectability * obj.category_weighing);
          } else {
            obj.total_score = Math.round(obj.impact * obj.probability * obj.detectability);
          }
        }
        updateObj = obj;
      }
      return obj;
    });
    setMonitoringDetails(clonedmonitoringDetails);

    if (updateMonitoringDetails?.length > 0) {
      const find = updateMonitoringDetails.find((obj: any) => obj.id == row.id);
      const clonedupdateMonitoringDetails: any = updateMonitoringDetails.map((obj: any) => {
        if (obj.id === row.id) {
          return updateObj;
        } else {
          return obj;
        }
      });
      if (!find) {
        setUpdateMonitoringDetails([...clonedupdateMonitoringDetails, updateObj]);
      } else {
        setUpdateMonitoringDetails(clonedupdateMonitoringDetails);
      }
    } else {
      setUpdateMonitoringDetails([updateObj]);
    }
  };

  const columns: GridColumns = [
    { field: 'questions_for_discussion', headerName: 'Questions for Discussion', minWidth: 180, flex: 1 },
    {
      field: 'impact',
      headerName: 'Impact',
      minWidth: 108,
      maxWidth: 128,
      flex: 1,
      renderCell: (params: any) => (
        <FormControl variant="standard" fullWidth>
          <Select
            labelId="impact"
            id="impact"
            onChange={e => handleChange(e, params.row)}
            label="impact"
            name="impact"
            value={params.row.impact}
            disabled={!isDisable}
          >
            <MenuItem value={3}>3 - High</MenuItem>
            <MenuItem value={2}>2 - Medium</MenuItem>
            <MenuItem value={1}>1 - Low</MenuItem>
          </Select>
        </FormControl>
      )
    },
    {
      field: 'probability',
      headerName: 'Probability',
      minWidth: 108,
      maxWidth: 128,
      flex: 1,
      renderCell: (params: any) => (
        <FormControl variant="standard" fullWidth>
          <Select
            labelId="probability"
            id="probability"
            onChange={e => handleChange(e, params.row)}
            label="probability"
            name="probability"
            value={params.row.probability}
            disabled={!isDisable}
          >
            <MenuItem value={3}>3 - High</MenuItem>
            <MenuItem value={2}>2 - Medium</MenuItem>
            <MenuItem value={1}>1 - Low</MenuItem>
          </Select>
        </FormControl>
      )
    },
    {
      field: 'detectability',
      headerName: 'Detectability',
      minWidth: 108,
      maxWidth: 128,
      flex: 1,
      renderCell: (params: any) => (
        <FormControl variant="standard" fullWidth>
          <Select
            labelId="detectability"
            id="detectability"
            onChange={e => handleChange(e, params.row)}
            label="detectability"
            name="detectability"
            value={params.row.detectability}
            disabled={!isDisable}
          >
            <MenuItem value={3}>3 - High</MenuItem>
            <MenuItem value={2}>2 - Medium</MenuItem>
            <MenuItem value={1}>1 - Low</MenuItem>
          </Select>
        </FormControl>
      )
    },
    {
      field: 'total_score',
      headerName: 'Total Score',
      minWidth: 72,
      maxWidth: 92,
      flex: 1
    },
    {
      field: 'category_weighing',
      headerName: 'Category Weighing',
      minWidth: 90,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.hierarchy.length === 1 ? (
          <FormControl variant="standard" fullWidth>
            <Select
              labelId="category_weighing"
              id="category_weighing"
              onChange={e => handleChange(e, params.row)}
              label="category_weighing"
              name="category_weighing"
              value={params.row.category_weighing}
              disabled={!isDisable}
            >
              <MenuItem value={0.1}>0.1</MenuItem>
              <MenuItem value={0.2}>0.2</MenuItem>
              <MenuItem value={0.3}>0.3</MenuItem>
              <MenuItem value={0.4}>0.4</MenuItem>
              <MenuItem value={0.5}>0.5</MenuItem>
              <MenuItem value={0.6}>0.6</MenuItem>
              <MenuItem value={0.7}>0.7</MenuItem>
              <MenuItem value={0.8}>0.8</MenuItem>
              <MenuItem value={0.9}>0.9</MenuItem>
              <MenuItem value={1}>1.0</MenuItem>
            </Select>
          </FormControl>
        ) : (
          ''
        );
      }
    }
  ];

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = row => row.hierarchy;

  const handleNextbtn = (_: any) => {
    setConfirmExitValue(dispatch, true);
    setTabVal(tabVal + 1);
  };

  const getMonitoringData = async () => {
    await getDataMonitoringData({
      study_id: studyId,
      application: process.env.REACT_APP_NAME
    });
  };

  useEffect(() => {
    setoverallRisk(0);
    setPriorityLevel('');
    getMonitoringData();
  }, [studyId]);

  useEffect(() => {
    const tempDetailsRows: any = [];
    if (monitoringData) {
      monitoringData.category_details.map((riskCategory: any) => {
        const rowObj: any = [];
        rowObj.push({
          hierarchy: [riskCategory.category_data.category],
          questions_for_discussion: riskCategory.category_data.questions_for_discussion,
          impact: riskCategory.category_data.impact,
          probability: riskCategory.category_data.probability,
          detectability: riskCategory.category_data.detectability,
          total_score: riskCategory.category_data.total_score,
          category_weighing: riskCategory.category_data.category_weighing,
          id: riskCategory.category_data.id
        });
        if (riskCategory.questions?.length > 0) {
          riskCategory.questions.map((riskQuery: any) => {
            rowObj.push({
              hierarchy: [riskCategory.category_data.category, riskQuery.category_number],
              questions_for_discussion: riskQuery.questions_for_discussion,
              impact: riskQuery.impact,
              probability: riskQuery.probability,
              detectability: riskQuery.detectability,
              total_score: riskQuery.total_score,
              category_weighing: riskQuery.category_weighing,
              id: riskQuery.id
            });
          });
        }
        tempDetailsRows.push(rowObj);
      });
      if (tempDetailsRows.length > 0) {
        setMonitoringDetails([].concat(...tempDetailsRows));
      } else {
        setMonitoringDetails(tempDetailsRows);
      }
    }
  }, [monitoringData]);

  const [overallRisk, setoverallRisk] = useState(0);
  const [prirorityLevel, setPriorityLevel] = useState('');
  useEffect(() => {
    if (monitoringDetails) {
      let riskcount = 0;
      const isTotalCountNull: boolean = monitoringDetails.some(
        (category: any) => category.total_score === null && category.hierarchy.length == 1
      );
      if (!isTotalCountNull) {
        monitoringDetails.map((riskCategory: any) => {
          if (riskCategory.hierarchy.length == 1) {
            riskcount = riskcount + riskCategory.total_score * riskCategory.category_weighing;
          }
        });
        setoverallRisk(Math.floor(riskcount));
        // let prirorityLevel;
        if (riskcount >= 234) {
          setPriorityLevel('High');
        } else if (riskcount < 234 && riskcount >= 117) {
          setPriorityLevel('Medium');
        } else if (riskcount <= 116 && riskcount >= 13) {
          setPriorityLevel('Low');
        } else {
          setPriorityLevel('');
        }
      }
    }
  }, [monitoringDetails]);

  const handleSave = async (_: any) => {
    if (updateMonitoringDetails.length > 0) {
      const obj = updateMonitoringDetails.map((row: any) => {
        return {
          id: row.id,
          impact: row.impact,
          probability: row.probability,
          detectability: row.detectability,
          total_score: row.total_score,
          category_weighing: row.category_weighing != null ? row.category_weighing : 0.0
        };
      });
      let updateData;
      if (overallRisk > 0) {
        updateData = {
          conduct_monitoring_update: [...obj],
          overall_risk: overallRisk,
          priority: prirorityLevel,
          updated_by: user
        };
      } else {
        updateData = {
          conduct_monitoring_update: [...obj],
          priority: prirorityLevel,
          updated_by: user
        };
      }
      try {
        await updateDataMonitoring({
          site_id: selectedSite,
          study_id: studyId,
          data: updateData,
          country: selectedCountry,
          application: process.env.REACT_APP_NAME
        });
        dispatchApp(setMessage({ msgDetails: 'Data Updated', severity: 'success' }));
        setConfirmExitValue(dispatch, false);
      } catch (ex) {
        dispatchApp(setMessage({ msgDetails: 'Error occered while saving the data', severity: 'success' }));
      }
    }
  };
  // const fromNavigationPathName: any = useAppSelector((state: any) => {
  //   return state.navigationPath.navigationPathName;
  // });

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'Category',
    minWidth: 188,
    maxWidth: 268,
    flex: 1,
    headerAlign: 'center'
  };
  const dataTableProps = {
    treeData: true,
    apiRef,
    getTreeDataPath,
    getRowHeight: () => 'auto',
    getEstimatedRowHeight: () => 200,
    groupingColDef,
    getRowClassName: (params: any) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'),
    sx: {
      cursor: 'pointer',
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'break-spaces',
        lineHeight: 1.3
      },
      '& .MuiDataGrid-treeDataGroupingCellToggle + span': { whiteSpace: 'normal !important' },
      '& .MuiDataGrid-cell': {
        paddingTop: '8px',
        paddingBottom: '8px',
        alignItems: 'flex-start'
      },
      '& .MuiDataGrid-row.Mui-even': { backgroundColor: '#5073B914' }
    },
    localeText:{footerTotalRows:'Total Row(s):'}
  };
  return (
    <div className="data-monitoring-page">
      <Typography variant="h5" component="div" sx={{ mb: 1.5, mt: -1, fontSize: '20px', fontWeight: 500 }}>
        Overall Risk Level: {overallRisk} &nbsp;
        {overallRisk >= 13 ? (
          <Chip
            label={prirorityLevel}
            size="small"
            color={prirorityLevel === 'High' ? 'error' : prirorityLevel === 'Medium' ? 'warning' : 'primary'}
          />
        ) : (
          ''
        )}
      </Typography>

      <Box
        sx={{
          height: '78.5vh',
          width: '100%',
          '@media (max-width:1367px)': {
            height: '68.5vh'
          }
        }}
      >
        <DataTable
          dataTableProps={dataTableProps}
          disableSelectionOnClick
          showMUIFeature={false}
          rows={monitoringDetails}
          hasDefinedColumns
          columns={columns}
          height="68.5vh"
          pagination={false}
          loading={monitoringDataLoading}
        />
      </Box>

      <Grid container spacing={2} className="bottom-navigation" mt={0}>
        <Grid item md={12} display="flex" justifyContent="flex-end">
          <Stack spacing={2} direction="row">
            <Button variant="contained" onClick={handleSave} disabled={!isDisable}>
              Save
            </Button>
            <Button variant="contained" onClick={handleNextbtn}>
              NEXT
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default DataMonitoringPage;
